import type { LocalPrivateSpecialistsConfig } from './config';

/**
 * Satisfaction smoothed toward a prior, so 100% from 1 review does not outrank 95% from 299.
 * score = (satisfaction * count + PRIOR * WEIGHT) / (count + WEIGHT)
 */
const PRIOR_SATISFACTION = 80;
const PRIOR_WEIGHT = 20;

export const smoothedSatisfaction = (satisfaction?: number | null, count?: number | null) => {
  if (satisfaction == null || !count) return null;
  return (satisfaction * count + PRIOR_SATISFACTION * PRIOR_WEIGHT) / (count + PRIOR_WEIGHT);
};

/**
 * Search-side pre-filter for "has an in-person turn": online-booking doctor (not phone/bulk) with a
 * first free turn in the index. The index can be stale (it still lists turns of doctors whose booking
 * just closed), so the final say is Saman's `booking_available` — see LocalPrivateSpecialists.
 */
export const hasIndexedInPersonTurn = (hit: any) => !hit?.is_bulk && !!hit?.presence_freeturn;

/** «<span>اولین نوبت: <b>کمتر از یک ساعت دیگر</b></span>» of the search's booking action → plain text. */
export const firstTurnLabel = (hit: any): string => {
  const action = (hit?.actions ?? []).find((item: any) => /اولین نوبت/.test(item?.top_title ?? ''));
  return (action?.top_title ?? '').replace(/<[^>]*>/g, '').trim();
};

const EARTH_RADIUS_KM = 6371;
const toRad = (deg: number) => (deg * Math.PI) / 180;
/** Coordinates farther than this from the city point are data errors (e.g. «اصفهان» offices at 340 km). */
const IMPLAUSIBLE_KM = 100;

export const haversineKm = (a: { lat: number; lon: number }, b: { lat: number; lon: number }) => {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
};

/**
 * Neighbourhood check (config.near): true when a center lies within `near.radiusKm`, or — for a center
 * whose coordinates are missing, the 1/1 placeholder or implausibly far — when its `city_name` is the
 * configured city. Any center type counts (office, clinic, hospital).
 */
export const isInNeighbourhood = (hit: any, config: LocalPrivateSpecialistsConfig): boolean => {
  const { near } = config;
  if (!near) return true;
  return (hit?.centers ?? []).some((center: any) => {
    const map = center?.map;
    const hasCoords = Number.isFinite(map?.lat) && Number.isFinite(map?.lon) && !(map.lat === 1 && map.lon === 1);
    const km = hasCoords ? haversineKm(near, { lat: map.lat, lon: map.lon }) : null;
    if (km != null && km <= IMPLAUSIBLE_KM) return km <= near.radiusKm;
    return center?.city_name === config.cityName;
  });
};

/** Quality floor (config.minSatisfaction / minRates); profiles without enough reviews are left out. */
export const meetsQualityFloor = (hit: any, config: LocalPrivateSpecialistsConfig): boolean => {
  if (config.minSatisfaction != null && !(Number(hit?.satisfaction) >= config.minSatisfaction)) return false;
  if (config.minRates != null && !(Number(hit?.rates_count) >= config.minRates)) return false;
  return true;
};

const firstTurnTime = (hit: any) => {
  const value = Number(hit?.presence_freeturn);
  return Number.isFinite(value) && value > 0 ? value : Number.POSITIVE_INFINITY;
};

/**
 * Keeps doctors of the configured specialty (minus excluded ones), drops the profile's own doctor,
 * duplicates across merged search pages, doctors outside the neighbourhood and below the quality
 * floor. Order: `first-turn` = earliest free turn, then smoothed satisfaction; otherwise smoothed
 * satisfaction (doctors without reviews last, in the search's own order).
 */
export const rankLocalSpecialists = (results: any[] | undefined, config: LocalPrivateSpecialistsConfig, ownSlug?: string) => {
  const ownUrl = ownSlug ? `/dr/${ownSlug}/` : null;
  const seen = new Set<string>();
  const doctors = (results ?? []).filter(hit => {
    if (hit?.type && hit.type !== 'doctor') return false;
    if (ownUrl && (hit?.url === ownUrl || hit?.slug === ownSlug)) return false;
    const key = hit?.url ?? hit?.id;
    if (key && seen.has(key)) return false;
    if (key) seen.add(key);
    const expertise: string = hit?.display_expertise ?? '';
    if (!config.includeExpertise.some(text => expertise.includes(text))) return false;
    if (config.excludeExpertise?.some(text => expertise.includes(text))) return false;
    return isInNeighbourhood(hit, config) && meetsQualityFloor(hit, config);
  });

  return doctors
    .map((hit, index) => ({ hit, index, score: smoothedSatisfaction(hit?.satisfaction, hit?.rates_count) }))
    .sort((a, b) => {
      if (config.order === 'first-turn') {
        const byTurn = firstTurnTime(a.hit) - firstTurnTime(b.hit);
        if (byTurn) return byTurn;
      }
      if (a.score == null && b.score == null) return a.index - b.index;
      if (a.score == null) return 1;
      if (b.score == null) return -1;
      return b.score - a.score || a.index - b.index;
    })
    .map(item => item.hit);
};

/** «دکتر  نادر   ترکاشوند» → «نادر ترکاشوند». */
const doctorNameKey = (hit: any) =>
  String(hit?.title ?? hit?.display_name ?? '')
    .replace(/^\s*دکتر\s+/, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * One card per doctor: many doctors have a second profile (office + clinic, e.g. Nader Torkashvand in
 * Malayer). Applied after the bookability check, so the first bookable profile in display order
 * (earliest turn) is the one kept.
 */
export const uniqueByDoctorName = <T>(doctors: T[]): T[] => {
  const seen = new Set<string>();
  return doctors.filter(hit => {
    const key = doctorNameKey(hit);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// ── Tiered mode (config.tiers, Dr. Asilian) ──────────────────────────────────────────────────────

export type SuggestionTier = 'clinic' | 'online' | 'office';

/** Online-visit center; its `city_name` is the platform's (تهران), not the doctor's. */
const ONLINE_VISIT_CENTER_ID = '5532';

type SamanStatus = {
  in_person_availability?: { booking_available?: boolean } | null;
  online_visit_availability?: { booking_available?: boolean } | null;
  centers?: { id: string; availability_status?: { booking_available?: boolean; nearest_time_slot?: string | null } }[];
} | null | undefined;

const slugKey = (hit: any) => {
  const raw = hit?.slug || hit?.url;
  return typeof raw === 'string' ? raw.split('/').filter(Boolean).pop() ?? '' : '';
};

const baseFilter = (hit: any, ownSlug: string | undefined, expertise: string[], exclude: string[] | undefined) => {
  if (hit?.type && hit.type !== 'doctor') return false;
  if (ownSlug && slugKey(hit) === ownSlug) return false;
  const display: string = hit?.display_expertise ?? '';
  if (!expertise.some(text => display.includes(text))) return false;
  if (exclude?.some(text => display.includes(text))) return false;
  return true;
};

/**
 * Pre-Saman candidates of each tier (quality floor applied to all; one entry per url per tier):
 * - clinic: works at one of `tiers.clinicCenterIds` (the profile doctor's own clinic);
 * - online: offers online visit (`consult_active_booking`) and has a non-online center in the city —
 *   distance does not matter for an online visit; `tiers.onlineIncludeExpertise` may admit residents;
 * - office: the regular neighbourhood list (rankLocalSpecialists) with an indexed in-person turn.
 */
export const tierCandidates = (results: any[] | undefined, config: LocalPrivateSpecialistsConfig, ownSlug?: string) => {
  const tiers = config.tiers;
  const unique = (hits: any[]) => {
    const seen = new Set<string>();
    return hits.filter(hit => {
      const key = hit?.url ?? hit?.id;
      if (!key || seen.has(key)) return !key;
      seen.add(key);
      return true;
    });
  };
  const list = results ?? [];
  const clinic = tiers
    ? unique(
        list.filter(
          hit =>
            baseFilter(hit, ownSlug, config.includeExpertise, config.excludeExpertise) &&
            meetsQualityFloor(hit, config) &&
            !hit?.is_bulk &&
            (hit?.centers ?? []).some((center: any) => tiers.clinicCenterIds.includes(String(center?.id))),
        ),
      )
    : [];
  const online = tiers
    ? unique(
        list.filter(
          hit =>
            baseFilter(hit, ownSlug, tiers.onlineIncludeExpertise, undefined) &&
            meetsQualityFloor(hit, config) &&
            hit?.consult_active_booking === true &&
            (hit?.centers ?? []).some(
              (center: any) => String(center?.id) !== ONLINE_VISIT_CENTER_ID && center?.city_name === config.cityName,
            ),
        ),
      )
    : [];
  const office = rankLocalSpecialists(list, config, ownSlug).filter(hasIndexedInPersonTurn);
  return { clinic, online, office };
};

const slotTime = (value?: string | null) => {
  const ms = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
};

const tehranDay = (unixSeconds: unknown) => {
  const value = Number(unixSeconds);
  if (!Number.isFinite(value) || value <= 0) return '9999-99-99';
  // Iran is UTC+03:30; compare calendar days there, not in UTC.
  return new Date((value + 3.5 * 3600) * 1000).toISOString().slice(0, 10);
};

const bySatisfaction = (a: any, b: any) =>
  (smoothedSatisfaction(b?.satisfaction, b?.rates_count) ?? -1) - (smoothedSatisfaction(a?.satisfaction, a?.rates_count) ?? -1);

/**
 * Final tiered list after the Saman check. Each hit is copied with `_tier` (and `_clinicSlot` for the
 * clinic tier). Order: clinic (bookable at the clinic itself; earliest clinic slot, then satisfaction)
 * → online (online visit bookable; same-day online turns are equal, then also bookable in person, then
 * satisfaction) → office (bookable in person; its own order). Each viewer's card then adapts to what
 * they ignore (viewerMemory.ts). One card per doctor:
 * a doctor in a higher tier is not repeated lower down.
 */
export const assembleTiers = (
  candidates: { clinic: any[]; online: any[]; office: any[] },
  samanBySlug: Record<string, SamanStatus>,
  config: LocalPrivateSpecialistsConfig,
) => {
  const clinicIds = config.tiers?.clinicCenterIds ?? [];
  const clinicStatus = (hit: any) =>
    samanBySlug[slugKey(hit)]?.centers?.find(center => clinicIds.includes(String(center.id)) && center.availability_status?.booking_available)
      ?.availability_status;

  const clinic = candidates.clinic
    .filter(hit => clinicStatus(hit))
    .map(hit => ({ ...hit, _tier: 'clinic' as SuggestionTier, _clinicSlot: clinicStatus(hit)?.nearest_time_slot ?? null }))
    .sort((a, b) => slotTime(a._clinicSlot) - slotTime(b._clinicSlot) || bySatisfaction(a, b));

  const inPerson = (hit: any) => samanBySlug[slugKey(hit)]?.in_person_availability?.booking_available === true;
  const online = candidates.online
    .filter(hit => samanBySlug[slugKey(hit)]?.online_visit_availability?.booking_available === true)
    .map(hit => ({ ...hit, _tier: 'online' as SuggestionTier }))
    .sort((a, b) => {
      const dayA = tehranDay(a.consult_freeturn);
      const dayB = tehranDay(b.consult_freeturn);
      if (dayA !== dayB) return dayA < dayB ? -1 : 1;
      return Number(inPerson(b)) - Number(inPerson(a)) || bySatisfaction(a, b);
    });

  const office = candidates.office.filter(inPerson).map(hit => ({ ...hit, _tier: 'office' as SuggestionTier }));

  return uniqueByDoctorName([...clinic, ...online, ...office]);
};
